import React, { useEffect, useRef, useState } from "react";
import { Modal, Slider, Button } from "antd";

const SIZE = 256;

type Props = {
  open: boolean;
  src: string | null;
  onCancel: () => void;
  onConfirm: (dataUrl: string) => void;
};

const AvatarCropper = ({ open, src, onCancel, onConfirm }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<any>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [offset, setOffset] = useState<any>({ x: 0, y: 0 });
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (!open || !src) return;
    setReady(false);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    const image = new Image();
    image.onload = () => {
      imgRef.current = image;
      setReady(true);
    };
    image.src = src;
  }, [open, src]);

  const draw = () => {
    const canvas = canvasRef.current;
    const image = imgRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.fillStyle = "#f4f6f5";
    ctx.fillRect(0, 0, SIZE, SIZE);
    const base = Math.max(SIZE / image.width, SIZE / image.height);
    const scale = base * zoom;
    const w = image.width * scale;
    const h = image.height * scale;
    const x = (SIZE - w) / 2 + offset.x;
    const y = (SIZE - h) / 2 + offset.y;
    ctx.drawImage(image, x, y, w, h);
  };

  useEffect(() => {
    if (ready) draw();
    // eslint-disable-next-line
  }, [ready, zoom, offset]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as any).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.ox + (e.clientX - dragRef.current.x),
      y: dragRef.current.oy + (e.clientY - dragRef.current.y),
    });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const confirm = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onConfirm(canvas.toDataURL("image/jpeg", 0.85));
  };

  return (
    <Modal
      visible={open}
      title="Ajustar foto"
      onCancel={onCancel}
      centered
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="ok" className="px-btn px-btn-primary" onClick={confirm}>
          Definir foto
        </Button>,
      ]}
    >
      <div className="avatar-cropper">
        <div
          className="avatar-cropper-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <canvas ref={canvasRef} width={SIZE} height={SIZE} />
        </div>
        <div className="avatar-cropper-hint">
          Arraste para posicionar e use o zoom abaixo.
        </div>
        <Slider min={1} max={3} step={0.01} value={zoom} onChange={(v: any) => setZoom(v)} />
      </div>
    </Modal>
  );
};

export default AvatarCropper;
